import { Injectable } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

/**
 * Breakpoints canónicos de la capa responsive del host (Fase P0).
 *
 * Estas media queries son IDÉNTICAS, literal por literal, a las que usa
 * @ngbracket/ngx-layout (fork de @angular/flex-layout ya activo en la app)
 * para sus directivas fxLayout.xs/.sm/.md/.lg/.xl — ver
 * node_modules/@ngbracket/ngx-layout/fesm2022/ngbracket-ngx-layout-core.mjs.
 * Se centralizan acá (en vez de usar los `Breakpoints.Handset/Tablet/Web` de
 * @angular/cdk/layout, que usan otros cortes) para que la lógica de runtime
 * (ej. modo del sidenav) coincida exactamente con los breakpoints que ya usan
 * los templates existentes vía fxShow/fxHide/fxLayout.
 *
 * Ver también src/assets/styles/_breakpoints.scss (equivalente SCSS) y
 * docs/responsive-implementation/ (fuente oficial + estrategia anti-divergencia
 * SCSS↔TS: `npm run test:responsive-tokens` valida que ambos coincidan).
 *
 * IMPORTANTE: no agregar nuevos breakpoints hardcodeados (`matchMedia('...')`,
 * `@media (...)` literal) fuera de este objeto y de `_breakpoints.scss`. Todo
 * desarrollo nuevo debe reutilizar `HOST_BREAKPOINTS`/los mixins `bp-*`.
 */
export type BreakpointName =
  | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  | 'ltSm' | 'ltMd' | 'ltLg'
  | 'gtXs' | 'gtSm' | 'gtMd';

export const HOST_BREAKPOINTS: Record<BreakpointName, string> = {
  xs: '(max-width: 599.98px)',
  sm: '(min-width: 600px) and (max-width: 959.98px)',
  md: '(min-width: 960px) and (max-width: 1279.98px)',
  lg: '(min-width: 1280px) and (max-width: 1919.98px)',
  xl: '(min-width: 1920px)',
  ltSm: '(max-width: 599.98px)',
  ltMd: '(max-width: 959.98px)',
  ltLg: '(max-width: 1279.98px)',
  gtXs: '(min-width: 600px)',
  gtSm: '(min-width: 960px)',
  gtMd: '(min-width: 1280px)',
};

/**
 * Corte usado por el shell (sidenav/toolbar) para decidir cuándo el drawer
 * debe comportarse como overlay (`mode="over"`) en vez de fijo
 * (`mode="side"`). Coincide con `bp-mobile-nav` en `_breakpoints.scss`.
 */
export const MOBILE_NAV_BREAKPOINT = HOST_BREAKPOINTS.ltMd;

@Injectable({ providedIn: 'root' })
export class BreakpointService {
  /** true en xs o sm (< 960px) — pantallas donde el sidenav debe ser overlay. */
  readonly isMobileNav$: Observable<boolean>;

  /** true solo en xs (< 600px) — teléfono. */
  readonly isHandset$: Observable<boolean>;

  /** true en sm o md (600px-1279.98px) — tablet portrait/landscape. */
  readonly isTablet$: Observable<boolean>;

  /** true en lg o xl (>= 1280px) — desktop. */
  readonly isDesktop$: Observable<boolean>;

  constructor(private breakpointObserver: BreakpointObserver) {
    this.isMobileNav$ = this.breakpointObserver
      .observe([MOBILE_NAV_BREAKPOINT])
      .pipe(
        map(result => result.matches),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    this.isHandset$ = this.breakpointObserver
      .observe([HOST_BREAKPOINTS.xs])
      .pipe(
        map(result => result.matches),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    this.isTablet$ = this.breakpointObserver
      .observe([HOST_BREAKPOINTS.sm, HOST_BREAKPOINTS.md])
      .pipe(
        map(result => result.matches),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    this.isDesktop$ = this.breakpointObserver
      .observe([HOST_BREAKPOINTS.lg, HOST_BREAKPOINTS.xl])
      .pipe(
        map(result => result.matches),
        shareReplay({ bufferSize: 1, refCount: true })
      );
  }
}
